// api.ts
import axios, { InternalAxiosRequestConfig, AxiosRequestHeaders } from "axios";
import { getAccessToken, getIdToken } from "./auth";
import {
  WEBFRONT_RUNTIME_CONFIG,
  type EndpointProfile,
} from "./runtimeConfig";

const activeEndpointProfile: EndpointProfile =
  WEBFRONT_RUNTIME_CONFIG.endpointProfiles[
    WEBFRONT_RUNTIME_CONFIG.initialDataEnvironment
  ];

export const getApiBaseUrl = () => activeEndpointProfile.apiBaseUrl;
export const getWeatherAgentUrl = () => activeEndpointProfile.weatherAgentUrl;
export const getMonitoringApiUrl = () => activeEndpointProfile.monitoringApiUrl;
export const getWeatherAgentWsUrl = () =>
  activeEndpointProfile.weatherAgentWsUrl;

// Main API client - uses ACCESS token for resource endpoints
export const api = axios.create({ baseURL: getApiBaseUrl() });

// Cognito API client - uses ID token for user management endpoints
export const cognitoApi = axios.create({ baseURL: getApiBaseUrl() });

export const weatherAgentApi = axios.create({ baseURL: getWeatherAgentUrl() });
export const monitoringApi = axios.create({ baseURL: getMonitoringApiUrl() });

api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const token = await getAccessToken();
  console.log(
    "[API Interceptor]",
    config.url,
    "Token:",
    token ? `${token.substring(0, 20)}...` : "NULL"
  );
  if (token) {
    // If Axios v1 has AxiosHeaders, use .set; else write to plain object
    const h = config.headers as any;
    if (h && typeof h.set === "function") {
      h.set("Authorization", `Bearer ${token}`);
    } else {
      config.headers = (config.headers || {}) as AxiosRequestHeaders;
      (config.headers as AxiosRequestHeaders)[
        "Authorization"
      ] = `Bearer ${token}`;
    }
  } else {
    console.warn("[API Interceptor] No token available for", config.url);
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error || {};
    if (response?.status === 401 && !config.__retried) {
      config.__retried = true;
      // force a refresh via getAccessToken (which refreshes if needed)
      const token = await getAccessToken(true);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return api.request(config);
      }
    }
    return Promise.reject(error);
  }
);

// Cognito API interceptors - use ID token
cognitoApi.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const token = await getIdToken();
    console.log(
      "[Cognito API Interceptor]",
      config.url,
      "ID Token:",
      token ? `${token.substring(0, 20)}...` : "NULL"
    );
    if (token) {
      const h = config.headers as any;
      if (h && typeof h.set === "function") {
        h.set("Authorization", `Bearer ${token}`);
      } else {
        config.headers = (config.headers || {}) as AxiosRequestHeaders;
        (config.headers as AxiosRequestHeaders)[
          "Authorization"
        ] = `Bearer ${token}`;
      }
    } else {
      console.warn(
        "[Cognito API Interceptor] No ID token available for",
        config.url
      );
    }
    return config;
  }
);

cognitoApi.interceptors.response.use(
  (r) => r,
  async (error) => {
    const { config, response } = error || {};
    if (response?.status === 401 && !config.__retried) {
      config.__retried = true;
      const token = await getIdToken(true);
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return cognitoApi.request(config);
      }
    }
    return Promise.reject(error);
  }
);

const attachAccessTokenInterceptors = (client: typeof weatherAgentApi) => {
  client.interceptors.request.use(
    async (config: InternalAxiosRequestConfig) => {
      const token = await getAccessToken();
      if (token) {
        const h = config.headers as any;
        if (h && typeof h.set === "function") {
          h.set("Authorization", `Bearer ${token}`);
        } else {
          config.headers = (config.headers || {}) as AxiosRequestHeaders;
          (config.headers as AxiosRequestHeaders)[
            "Authorization"
          ] = `Bearer ${token}`;
        }
      }
      return config;
    }
  );

  client.interceptors.response.use(
    (r) => r,
    async (error) => {
      const { config, response } = error || {};
      if (response?.status === 401 && !config.__retried) {
        config.__retried = true;
        const token = await getAccessToken(true);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          return client.request(config);
        }
      }
      return Promise.reject(error);
    }
  );
};

attachAccessTokenInterceptors(weatherAgentApi);
attachAccessTokenInterceptors(monitoringApi);
